import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "text-sm font-medium select-none",
    "transition-[background,border,color,box-shadow,transform] duration-180 ease-out-expo",
    "focus-visible:outline-none focus-visible:shadow-focus",
    "disabled:pointer-events-none disabled:opacity-40",
    "active:scale-[0.985]",
    "[&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        // Primary = Electric Blue
        default:
          "bg-primary text-primary-foreground hover:bg-[hsl(217_91%_64%)] shadow-soft",
        // Secondary = surface élevée subtile
        secondary:
          "bg-white/[0.04] text-foreground hover:bg-white/[0.07] border border-white/[0.06]",
        // Outline ghost (border only)
        outline:
          "bg-transparent text-foreground hover:bg-white/[0.04] border border-white/[0.08]",
        // Ghost (no bg, hover slight)
        ghost: "text-foreground hover:bg-white/[0.05]",
        // Link
        link: "text-primary underline-offset-4 hover:underline",
        // Destructive
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-[hsl(0_72%_55%)]",
        // Success
        success:
          "bg-success text-success-foreground hover:opacity-90",
      },
      size: {
        sm: "h-8 px-3 text-xs rounded-md",
        default: "h-10 px-4 rounded-lg",
        lg: "h-11 px-5 rounded-lg text-[15px] font-semibold",
        xl: "h-12 px-6 rounded-xl text-[15px] font-semibold",
        icon: "size-10 rounded-lg",
        "icon-sm": "size-8 rounded-md",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Spinner = () => (
  <span className="inline-block size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" />
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    if (asChild) {
      const child = React.Children.only(children) as React.ReactElement;
      const cloned = loading
        ? React.cloneElement(child, {
            children: (
              <>
                <Spinner />
                {(child.props as { children?: React.ReactNode }).children}
              </>
            ),
          })
        : child;
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {cloned}
        </Slot>
      );
    }
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Spinner /> : null}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";
export { buttonVariants };
